import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { FiRefreshCcw, FiDownload, FiSearch } from 'react-icons/fi';
import useAppRefresh from '../../hooks/useAppRefresh';

/**
 * Audit Log — v1.9.125
 *
 * Every noteworthy mutation (CR / PV / member / subscription create · edit ·
 * delete · approve · reject · pay) is logged by the backend's logAudit()
 * helper. This page is a thin viewer: date range + user + action + entity
 * filters + free-text search, with CSV export. Admin + Finance only.
 */

const ACTION_COLOR = {
  create:          { bg: '#dcfce7', fg: '#166534' },
  update:          { bg: '#e0e7ff', fg: '#3730a3' },
  delete:          { bg: '#fee2e2', fg: '#991b1b' },
  delete_request:  { bg: '#fef3c7', fg: '#92400e' },
  delete_rejected: { bg: '#f3f4f6', fg: '#4b5563' },
  approve:         { bg: '#dcfce7', fg: '#166534' },
  finance_approve: { bg: '#cffafe', fg: '#155e75' },
  reject:          { bg: '#fee2e2', fg: '#991b1b' },
  pay:             { bg: '#dcfce7', fg: '#166534' },
};
const pill = (action) => {
  const c = ACTION_COLOR[action] || { bg: '#f3f4f6', fg: '#4b5563' };
  return { padding: '2px 8px', borderRadius: 10, background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 };
};

const AuditLog = () => {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo]     = useState(today);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [userId, setUserId] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ actions: [], entities: [], users: [] });
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/audit', { params: { from, to, action, entity_type: entityType, user_id: userId, q, limit: 500 } })
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  const loadFilters = () => {
    api.get('/audit/filters').then(r => setFilters(r.data || { actions: [], entities: [], users: [] })).catch(() => {});
  };

  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, action, entityType, userId, q]);
  useAppRefresh(load);

  const exportCsv = () => {
    const header = ['When', 'User', 'Role', 'Action', 'Entity', 'Label', 'Details', 'IP'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      header.join(','),
      ...rows.map(r => [r.at, r.user_name || 'system', r.user_role || '', r.action, r.entity_type, r.entity_label || '', r.details || '', r.ip || ''].map(esc).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtWhen = (s) => {
    if (!s) return '—';
    // SQLite datetime('now') emits UTC without a Z — append it so the browser
    // treats the timestamp as UTC and renders in the viewer's local timezone.
    const d = new Date(s.includes('T') || s.endsWith('Z') ? s : s.replace(' ', 'T') + 'Z');
    return isNaN(d) ? s : d.toLocaleString();
  };

  const totalRows = useMemo(() => rows.length, [rows]);

  const inp = { width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Audit Log</h1><p>{totalRows} entries</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={load} disabled={loading}><FiRefreshCcw /> Reload</button>
          <button className="btn btn-primary" onClick={exportCsv} disabled={!rows.length}><FiDownload /> Export CSV</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, alignItems: 'end', marginBottom: 14 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>From</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>To</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>User</span>
          <select value={userId} onChange={e => setUserId(e.target.value)} style={inp}>
            <option value="">All</option>
            {filters.users.map(u => <option key={u.user_id} value={u.user_id}>{u.user_name}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Action</span>
          <select value={action} onChange={e => setAction(e.target.value)} style={inp}>
            <option value="">All</option>
            {filters.actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Entity</span>
          <select value={entityType} onChange={e => setEntityType(e.target.value)} style={inp}>
            <option value="">All</option>
            {filters.entities.map(e => <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: 'span 2' }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Search</span>
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="CR-0092, PV-0052, member name…" style={{ ...inp, paddingLeft: 32 }} />
          </div>
        </label>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>When</th><th>User</th><th>Action</th><th>Entity</th><th>Label</th><th>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6}><div className="empty-state"><p>{loading ? 'Loading…' : 'No audit entries in this range.'}</p></div></td></tr>
            ) : rows.map(r => (
              <tr key={r.id}>
                <td data-label="When" style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#374151' }}>{fmtWhen(r.at)}</td>
                <td data-label="User" style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 600 }}>{r.user_name || <em style={{ color: '#9ca3af' }}>system</em>}</div>
                  {r.user_role && <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'capitalize' }}>{r.user_role}</div>}
                </td>
                <td data-label="Action"><span style={pill(r.action)}>{r.action.replace(/_/g, ' ')}</span></td>
                <td data-label="Entity" style={{ fontSize: 12, color: '#4b5563', textTransform: 'capitalize' }}>{(r.entity_type || '').replace(/_/g, ' ')}</td>
                <td data-label="Label" style={{ fontSize: 12, fontFamily: 'monospace', color: '#111827' }}>{r.entity_label || '—'}</td>
                <td data-label="Details" style={{ fontSize: 12, color: '#6b7280' }}>{r.details || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLog;
